import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { FaMapPin } from "react-icons/fa";
import { getListingByUserID, getUserByID } from "../utils";
import UserAvatar from "../UserAvatar";

const fallbackImage =
  "https://images.pexels.com/photos/19415811/pexels-photo-19415811/free-photo-of-back-view-of-man-in-trench-coat-and-hat.jpeg?auto=compress&cs=tinysrgb&w=600";

const getOwnerName = (owner) =>
  `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() || "Boat Owner";

const getStartingPrice = (listing) =>
  listing?.pricing?.tripsStartingFrom ||
  listing?.pricing?.hourlyRate ||
  listing?.pricing?.hourlyOptions?.[0]?.price ||
  null;

export default function BoatOwnerBoats() {
  const { ownerId } = useParams();
  const [owner, setOwner] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchOwnerBoats = async () => {
      if (!ownerId) return;

      try {
        setLoading(true);
        const [ownerDetails, ownerListings] = await Promise.all([
          getUserByID(ownerId),
          getListingByUserID(ownerId),
        ]);

        if (!isMounted) return;

        setOwner(ownerDetails || null);
        setListings(Array.isArray(ownerListings) ? ownerListings : []);
      } catch (error) {
        console.error("Error fetching owner boats:", error);
        if (isMounted) {
          setListings([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOwnerBoats();

    return () => {
      isMounted = false;
    };
  }, [ownerId]);

  const activeBoats = useMemo(
    () =>
      listings
        .filter(
          (listing) =>
            listing?.boatStatus === "Active" && listing?.boatListed === true
        )
        .sort((a, b) => (b?.id || "").localeCompare(a?.id || "")),
    [listings]
  );

  const ownerName = getOwnerName(owner);

  return (
    <section className="cs-bg-dark text-white py-5" style={{ minHeight: "70vh" }}>
      <Container>
        <div className="d-flex align-items-center gap-3 mb-4">
          <UserAvatar
            src={owner?.avatar}
            name={ownerName}
            size={64}
            style={{ backgroundColor: "#334155" }}
          />
          <div>
            <h1 className="h3 mb-1">{ownerName}</h1>
            <p className="mb-0 text-white-50">
              {loading ? "Loading active boats..." : `${activeBoats.length} active boat${activeBoats.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : activeBoats.length === 0 ? (
          <div className="bg-white text-dark p-4" style={{ borderRadius: "8px" }}>
            No active boats are available for this owner right now.
          </div>
        ) : (
          <Row>
            {activeBoats.map((listing) => {
              const startingPrice = getStartingPrice(listing);

              return (
                <Col xs={12} md={6} lg={4} className="mb-4" key={listing.id}>
                  <Link
                    to={`/listing/${listing.id}`}
                    className="d-flex flex-column h-100"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div
                      style={{
                        minHeight: "240px",
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.18)), url(${listing.featured_image || fallbackImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "8px 8px 0 0",
                      }}
                    />
                    <div
                      className="bg-white text-dark p-3 flex-grow-1"
                      style={{ borderRadius: "0 0 8px 8px" }}
                    >
                      <h2 className="h5 mb-2">{listing.listingTitle || "Boat listing"}</h2>
                      <div className="d-flex align-items-center text-muted mb-2" style={{ gap: "6px" }}>
                        <FaMapPin />
                        <span>{listing.storageAddressText || "Location available on listing"}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">{listing.boatType || "Boat"}</span>
                        {startingPrice && (
                          <strong>US ${startingPrice}</strong>
                        )}
                      </div>
                    </div>
                  </Link>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
    </section>
  );
}
